import { useEffect } from 'react';
import { EMBEDDED, MessageMedium } from 'constants/messageMediums';
import { CONFIRM_DISPLAY_PRIORITY_MODAL } from 'modules/campaigns/lib/modals/ConfirmDefaultDisplayPriorityModal';
import { useDispatch } from 'react-redux';
import noop from 'utils/noop';
import { useDisplayPriorityContext } from 'modules/placements/context/DisplayPriorityContext';
import { closeModal, openModal } from 'modules/workflows/ducks';
import { SendNodeData } from 'modules/workflows/types';
import { BaseSendEditorProps } from '../BaseSendEditor.types';
import useCampaign from './useCampaign';
import useFeatureFlags from './useFeatureFlags';

/**
 * Hook used to contain all Embedded Editor-specific logic
 */
export const useUpdateEmbeddedEditor = ({
  nodeData,
  messageMedium,
  onDisplayPriorityOpen = noop,
  embeddedPriorityOrder: updatedEmbeddedPriorityOrder,
}: BaseSendEditorProps) => {
  const dispatch = useDispatch();

  const { campaign, updateCampaign } = useCampaign();
  const { embeddedPriorityOrder } = campaign;
  const { campaignId } = nodeData as Partial<SendNodeData>;

  useEffect(() => {
    if (updatedEmbeddedPriorityOrder) {
      // Display priority is saved when the user clicks "Save" in DisplayPriorityTakeover
      // so we need to update local campaign state to match (but not isDirty state)
      updateCampaign({ embeddedPriorityOrder: updatedEmbeddedPriorityOrder }, false);
    }
  }, [updatedEmbeddedPriorityOrder]);

  const {
    reset: setInitialDisplayPriority,
    saveDisplayPriority,
    campaignDisplayPriorityOrder,
  } = useDisplayPriorityContext();

  const { isEmbeddedMessagingJourneysEnabled } = useFeatureFlags();

  const isEmbeddedMedium =
    isEmbeddedMessagingJourneysEnabled && messageMedium === (EMBEDDED as unknown as MessageMedium);

  /** If a user just created their campaign but hasn't set their display priority,
   * we should prompt them to set it before they can continue
   */
  const shouldConfirmDefaultDisplayPriority =
    isEmbeddedMedium && !!campaignId && !embeddedPriorityOrder;

  /** Display Priority Modal callbacks ---------------------------- */
  const handleCloseModal = () => dispatch(closeModal(CONFIRM_DISPLAY_PRIORITY_MODAL));

  const handleDefaultDisplayPriorityConfirm = (onConfirm?: () => void) => {
    setInitialDisplayPriority();
    saveDisplayPriority();
    handleCloseModal();
    if (onConfirm) onConfirm();
  };

  const handleDefaultDisplayPrioritySelect = () => {
    handleCloseModal();
    onDisplayPriorityOpen();
  };

  const openDefaultDisplayPriorityModal = ({ onConfirm = noop }: { onConfirm?: () => void }) => {
    dispatch(
      openModal(CONFIRM_DISPLAY_PRIORITY_MODAL, {
        campaignDisplayPriorityOrder,
        onConfirm: () => handleDefaultDisplayPriorityConfirm(onConfirm),
        onClose: handleCloseModal,
        onSelectDisplayPriority: handleDefaultDisplayPrioritySelect,
      }),
    );
  };

  return {
    openDefaultDisplayPriorityModal,
    shouldConfirmDefaultDisplayPriority,
  };
};
